/**
 * Email Service for Travel Form Notifications
 * 
 * This service handles sending emails via Azure Function proxy to Logic Apps.
 * The Azure Function handles CORS and forwards requests to Logic App.
 * 
 * Architecture:
 * Browser → Azure Function (SendEmailFunction) → Azure Logic App → Office 365
 */

interface EmailPayload {
    to: string;
    subject: string;
    body: string;
    cc?: string;
    bcc?: string;
    attachments?: Array<{
        filename: string;
        content: string; // base64 encoded
    }>;
}

// Use Azure Function endpoint instead of direct Logic App URL
const EMAIL_API_URL = '/api/SendEmailFunction';

/**
 * Send email via Azure Logic App
 */
async function sendEmail(payload: EmailPayload): Promise<boolean> {
    console.log('📧 sendEmail called with payload:', JSON.stringify({
        to: payload.to,
        subject: payload.subject,
        bodyLength: payload.body?.length || 0,
        hasAttachments: !!payload.attachments,
        attachmentsCount: payload.attachments?.length || 0
    }, null, 2));
    console.log('📧 Email API URL:', EMAIL_API_URL);
    console.log('📧 Full payload:', JSON.stringify(payload, null, 2));

    try {
        console.log('📧 Sending email via Azure Function proxy...');
        const response = await fetch(EMAIL_API_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(payload)
        });

        console.log('Email API response status:', response.status);

        if (!response.ok) {
            const errorText = await response.text();
            console.error('Email API error response:', errorText);
            throw new Error(`Email send failed: ${response.status}`);
        }

        console.log('✅ Email sent successfully:', payload.subject);
        return true;
    } catch (error) {
        console.error('❌ Failed to send email:', error);
        return false;
    }
}

/**
 * Send approval notification to user
 */
export async function sendApprovalEmail(form: {
    owner_email: string;
    owner_name: string;
    project_number: string;
    project_name: string;
    client: string;
    travel_date_from: string;
    travel_date_to: string;
    approved_by: string;
    travel_type?: 'project' | 'travel';
}): Promise<boolean> {
    const isProjectTravel = form.travel_type !== 'travel';
    const travelTypeLabel = isProjectTravel ? 'Project Travel' : 'Travel';
    const subject = isProjectTravel 
        ? `✅ Travel Request Approved - ${form.project_number}`
        : `✅ Travel Request Approved`;
    
    const projectRows = isProjectTravel ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Project:</strong></td>
                    <td>${form.project_name} (${form.project_number})</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Client:</strong></td>
                    <td>${form.client}</td>
                </tr>` : '';
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #28a745;">✅ Your Travel Request Has Been Approved!</h2>
        
        <p>Hi ${form.owner_name},</p>
        
        <p>Great news! Your travel request has been approved.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Travel Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Type:</strong></td>
                    <td>${travelTypeLabel}</td>
                </tr>${projectRows}
                <tr>
                    <td style="padding: 5px 0;"><strong>Travel Dates:</strong></td>
                    <td>${new Date(form.travel_date_from).toLocaleDateString()} - ${new Date(form.travel_date_to).toLocaleDateString()}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Approved By:</strong></td>
                    <td>${form.approved_by}</td>
                </tr>
            </table>
        </div>
        
        <p>You can now proceed with your travel arrangements. Safe travels!</p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Travel Management System.<br>
            If you have any questions, please contact travel@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: form.owner_email,
        subject,
        body
    });
}

/**
 * Send reminder to user to complete their draft form
 */
export async function sendReminderEmail(form: {
    owner_email: string;
    owner_name: string;
    project_number: string;
    project_name: string;
    travel_date_from: string;
    travel_date_to: string;
    travel_type?: 'project' | 'travel';
}): Promise<boolean> {
    const isProjectTravel = form.travel_type !== 'travel';
    const travelTypeLabel = isProjectTravel ? 'Project Travel' : 'Travel';
    const subject = isProjectTravel 
        ? `⏰ Reminder: Complete Your Travel Form - ${form.project_number}`
        : `⏰ Reminder: Complete Your Travel Form`;
    
    const projectRow = isProjectTravel ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Project:</strong></td>
                    <td>${form.project_name} (${form.project_number})</td>
                </tr>` : '';
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #ff9800;">⏰ Reminder: Complete Your Travel Form</h2>
        
        <p>Hi ${form.owner_name},</p>
        
        <p>This is a friendly reminder to complete your travel form.</p>
        
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Incomplete Travel Form</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Type:</strong></td>
                    <td>${travelTypeLabel}</td>
                </tr>${projectRow}
                <tr>
                    <td style="padding: 5px 0;"><strong>Travel Dates:</strong></td>
                    <td>${new Date(form.travel_date_from).toLocaleDateString()} - ${new Date(form.travel_date_to).toLocaleDateString()}</td>
                </tr>
            </table>
        </div>
        
        <p>Please complete and submit your travel form as soon as possible.</p>
        
        <p>
            <a href="https://iod-dochub.iodparc.com/travel-form" 
               style="display: inline-block; padding: 10px 20px; background-color: #ff9800; color: white; text-decoration: none; border-radius: 5px;">
                Complete Travel Form
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated reminder from the IOD Travel Management System.<br>
            If you have any questions, please contact travel@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: form.owner_email,
        subject,
        body
    });
}

/**
 * Send rejection notification to user
 */
export async function sendRejectionEmail(form: {
    owner_email: string;
    owner_name: string;
    project_number: string;
    project_name: string;
    travel_date_from: string;
    travel_date_to: string;
    rejection_reason: string;
    travel_type?: 'project' | 'travel';
}): Promise<boolean> {
    const isProjectTravel = form.travel_type !== 'travel';
    const travelTypeLabel = isProjectTravel ? 'Project Travel' : 'Travel';
    const subject = isProjectTravel 
        ? `❌ Travel Request Requires Changes - ${form.project_number}`
        : `❌ Travel Request Requires Changes`;
    
    const projectInfo = isProjectTravel 
        ? `<p><strong>Project:</strong> ${form.project_name} (${form.project_number})</p>` 
        : '';
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #dc3545;">Travel Request Requires Changes</h2>
        
        <p>Hi ${form.owner_name},</p>
        
        <p>Your travel request requires some changes before it can be approved.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Travel Details</h3>
            <p><strong>Type:</strong> ${travelTypeLabel}</p>${projectInfo}
            <p><strong>Travel Dates:</strong> ${new Date(form.travel_date_from).toLocaleDateString()} - ${new Date(form.travel_date_to).toLocaleDateString()}</p>
        </div>
        
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Reason for Changes Required:</h3>
            <p style="margin: 0;">${form.rejection_reason}</p>
        </div>
        
        <p>Please update your travel form and resubmit for approval.</p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Travel Management System.<br>
            If you have any questions, please contact travel@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: form.owner_email,
        subject,
        body,
        cc: 'travel@iodparc.com'
    });
}

/**
 * Send submission notification to travel ops
 */
export async function sendSubmissionNotification(form: {
    owner_email: string;
    owner_name: string;
    project_number: string;
    project_name: string;
    client: string;
    travel_date_from: string;
    travel_date_to: string;
    id: number;
    travel_type?: 'project' | 'travel';
}): Promise<boolean> {
    const isProjectTravel = form.travel_type !== 'travel';
    const travelTypeLabel = isProjectTravel ? 'Project Travel' : 'Travel';
    const subject = isProjectTravel 
        ? `🆕 New Travel Request - ${form.owner_name} - ${form.project_number}`
        : `🆕 New Travel Request - ${form.owner_name}`;
    
    const projectRows = isProjectTravel ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Project:</strong></td>
                    <td>${form.project_name} (${form.project_number})</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Client:</strong></td>
                    <td>${form.client}</td>
                </tr>` : '';
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #007bff;">New Travel Request Submitted</h2>
        
        <p>A new travel request has been submitted and is awaiting approval.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Request Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Staff Member:</strong></td>
                    <td>${form.owner_name}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Email:</strong></td>
                    <td>${form.owner_email}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Type:</strong></td>
                    <td>${travelTypeLabel}</td>
                </tr>${projectRows}
                <tr>
                    <td style="padding: 5px 0;"><strong>Travel Dates:</strong></td>
                    <td>${new Date(form.travel_date_from).toLocaleDateString()} - ${new Date(form.travel_date_to).toLocaleDateString()}</td>
                </tr>
            </table>
        </div>
        
        <p>
            <a href="https://iod-dochub.iodparc.com/travel-dashboard" 
               style="display: inline-block; padding: 10px 20px; background-color: #007bff; color: white; text-decoration: none; border-radius: 5px;">
                Review in Dashboard
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Travel Management System.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'travel@iodparc.com',
        subject,
        body
    });
}

/**
 * Send whistleblower report to whistleblow@iodparc.com
 */
export async function sendWhistleblowerReport(report: {
    dateOfReport: string;
    contactDetails: string;
    natureOfConcern: string;
    submitterEmail: string;
    submitterName: string;
}): Promise<boolean> {
    const subject = `🔔 Whistleblower Report - ${new Date(report.dateOfReport).toLocaleDateString()}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #009688;">Whistleblower Report</h2>
        
        <p>A new whistleblower report has been submitted.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Report Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Date of Report:</strong></td>
                    <td>${new Date(report.dateOfReport).toLocaleDateString()}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Submitted By:</strong></td>
                    <td>${report.submitterName} (${report.submitterEmail})</td>
                </tr>
                ${report.contactDetails ? `
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Contact Details:</strong></td>
                    <td style="white-space: pre-wrap;">${report.contactDetails}</td>
                </tr>
                ` : ''}
            </table>
        </div>
        
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Nature of Concern or Incident</h3>
            <p style="white-space: pre-wrap; margin: 0;">${report.natureOfConcern}</p>
        </div>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Whistleblower Reporting System.<br>
            All concerns will be investigated as necessary depending on the nature of the concern.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'whistleblow@iodparc.com',
        subject,
        body
    });
}

/**
 * Send anonymous message to eot@iodparc.com
 */
export async function sendEOTAnonymousMessage(payload: {
    message: string;
}): Promise<boolean> {
    const subject = 'Anonymous Message to EOT';
    const escapedMessage = payload.message
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
        .replace(/\n/g, '<br>');

    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #009688;">Anonymous Message to EOT</h2>
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; white-space: normal;">
            <p style="margin: 0;">${escapedMessage}</p>
        </div>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'eot@iodparc.com',
        subject,
        body
    });
}

/**
 * Send event approval request to bgt@iodparc.com
 */
export async function sendEventApprovalRequest(eventData: {
    eventTitle: string;
    dates: string;
    location: string;
    eventOrganisers: string;
    typeOfEvent: string;
    eventOverview: string;
    relevanceToIOD: string;
    expectedBenefits: string;
    proposedOutputs: string;
    participants: Array<{
        name: string;
        workstream: string;
        roleInterest: string;
        fundingSource: string;
    }>;
    costs: Array<{
        category: string;
        details: string;
        estimatedCost: string;
    }>;
    plannedCommunications: string;
    leadForCommunications: string;
    followUpActions: string;
    submitterEmail: string;
    submitterName: string;
    totalCost: string;
}): Promise<boolean> {
    const subject = `📅 Event Approval Request - ${eventData.eventTitle}`;
    
    const participantsTable = eventData.participants
        .filter(p => p.name || p.workstream || p.roleInterest || p.fundingSource)
        .map(p => `
            <tr>
                <td style="padding: 8px; border: 1px solid #ddd;">${p.name || '-'}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${p.workstream || '-'}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${p.roleInterest || '-'}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${p.fundingSource || '-'}</td>
            </tr>
        `).join('');

    const costsTable = eventData.costs
        .filter(c => c.details || c.estimatedCost)
        .map(c => `
            <tr>
                <td style="padding: 8px; border: 1px solid #ddd;">${c.category}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${c.details || '-'}</td>
                <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">${c.estimatedCost ? '£' + c.estimatedCost : '-'}</td>
            </tr>
        `).join('');
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 800px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #009688;">External Event Approval Request</h2>
        
        <p>A new event approval request has been submitted.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Submitted By:</strong></td>
                    <td>${eventData.submitterName} (${eventData.submitterEmail})</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Submission Date:</strong></td>
                    <td>${new Date().toLocaleDateString()}</td>
                </tr>
            </table>
        </div>

        <h3 style="color: #009688; margin-top: 30px;">Section 1: Event Details</h3>
        <table style="width: 100%; margin-bottom: 20px;">
            <tr>
                <td style="padding: 5px 0; vertical-align: top; width: 200px;"><strong>Event Title:</strong></td>
                <td style="padding: 5px 0;">${eventData.eventTitle}</td>
            </tr>
            <tr>
                <td style="padding: 5px 0; vertical-align: top;"><strong>Dates:</strong></td>
                <td style="padding: 5px 0;">${eventData.dates}</td>
            </tr>
            <tr>
                <td style="padding: 5px 0; vertical-align: top;"><strong>Location:</strong></td>
                <td style="padding: 5px 0;">${eventData.location}</td>
            </tr>
            ${eventData.eventOrganisers ? `
            <tr>
                <td style="padding: 5px 0; vertical-align: top;"><strong>Event Organiser(s):</strong></td>
                <td style="padding: 5px 0;">${eventData.eventOrganisers}</td>
            </tr>
            ` : ''}
            ${eventData.typeOfEvent ? `
            <tr>
                <td style="padding: 5px 0; vertical-align: top;"><strong>Type of Event:</strong></td>
                <td style="padding: 5px 0;">${eventData.typeOfEvent}</td>
            </tr>
            ` : ''}
            ${eventData.eventOverview ? `
            <tr>
                <td style="padding: 5px 0; vertical-align: top;"><strong>Event Overview:</strong></td>
                <td style="padding: 5px 0; white-space: pre-wrap;">${eventData.eventOverview}</td>
            </tr>
            ` : ''}
        </table>

        <h3 style="color: #009688; margin-top: 30px;">Section 2: Rationale for Participation</h3>
        ${eventData.relevanceToIOD ? `
        <div style="margin-bottom: 15px;">
            <strong>Relevance to IOD PARC / Workstreams / BGT:</strong>
            <p style="white-space: pre-wrap; margin: 5px 0;">${eventData.relevanceToIOD}</p>
        </div>
        ` : ''}
        ${eventData.expectedBenefits ? `
        <div style="margin-bottom: 15px;">
            <strong>Expected Benefits:</strong>
            <p style="white-space: pre-wrap; margin: 5px 0;">${eventData.expectedBenefits}</p>
        </div>
        ` : ''}
        ${eventData.proposedOutputs ? `
        <div style="margin-bottom: 15px;">
            <strong>Proposed Outputs:</strong>
            <p style="white-space: pre-wrap; margin: 5px 0;">${eventData.proposedOutputs}</p>
        </div>
        ` : ''}

        ${participantsTable ? `
        <h3 style="color: #009688; margin-top: 30px;">Section 3: Participants</h3>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
                <tr style="background-color: #009688; color: white;">
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Name</th>
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Workstream</th>
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Role/Interest</th>
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Funding Source</th>
                </tr>
            </thead>
            <tbody>
                ${participantsTable}
            </tbody>
        </table>
        ` : ''}

        <h3 style="color: #009688; margin-top: 30px;">Section 4: Estimated Costs</h3>
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
            <thead>
                <tr style="background-color: #009688; color: white;">
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Category</th>
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: left;">Details</th>
                    <th style="padding: 10px; border: 1px solid #ddd; text-align: right;">Estimated Cost (£)</th>
                </tr>
            </thead>
            <tbody>
                ${costsTable}
                <tr style="background-color: #f8f9fa; font-weight: bold;">
                    <td colspan="2" style="padding: 10px; border: 1px solid #ddd; text-align: right;">Total Estimated Cost</td>
                    <td style="padding: 10px; border: 1px solid #ddd; text-align: right;">£${eventData.totalCost}</td>
                </tr>
            </tbody>
        </table>

        <h3 style="color: #009688; margin-top: 30px;">Section 5: Communications and Follow up</h3>
        ${eventData.plannedCommunications ? `
        <div style="margin-bottom: 15px;">
            <strong>Planned Communications Outputs:</strong>
            <p style="white-space: pre-wrap; margin: 5px 0;">${eventData.plannedCommunications}</p>
        </div>
        ` : ''}
        ${eventData.leadForCommunications ? `
        <div style="margin-bottom: 15px;">
            <strong>Lead for Communications:</strong>
            <p style="margin: 5px 0;">${eventData.leadForCommunications}</p>
        </div>
        ` : ''}
        ${eventData.followUpActions ? `
        <div style="margin-bottom: 15px;">
            <strong>Follow-up Actions / Reporting:</strong>
            <p style="white-space: pre-wrap; margin: 5px 0;">${eventData.followUpActions}</p>
        </div>
        ` : ''}
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Event Approval System.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'bgt@iodparc.com',
        subject,
        body
    });
}

/**
 * Send business case submission notification to business_case_approval@iodparc.com and joanne@iodparc.com
 */
export async function sendBusinessCaseSubmission(businessCase: {
    owner_email: string;
    owner_name: string;
    opportunity_name: string;
    client: string;
    proposal_coordinator: string;
    country_region?: string;
    duration_of_project?: string;
    budget?: string;
    deadline?: string;
    attachment_filename?: string;
    id: number;
}): Promise<boolean> {
    const subject = `🆕 New Business Case Submission - ${businessCase.opportunity_name}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #009688;">New Business Case Submitted</h2>
        
        <p>A new business case has been submitted and is awaiting approval.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Submission Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Submitted By:</strong></td>
                    <td>${businessCase.owner_name}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Email:</strong></td>
                    <td>${businessCase.owner_email}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Opportunity:</strong></td>
                    <td>${businessCase.opportunity_name}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Client:</strong></td>
                    <td>${businessCase.client}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Proposal Coordinator:</strong></td>
                    <td>${businessCase.proposal_coordinator}</td>
                </tr>
                ${businessCase.country_region ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Country/Region:</strong></td>
                    <td>${businessCase.country_region}</td>
                </tr>
                ` : ''}
                ${businessCase.budget ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Budget:</strong></td>
                    <td>${businessCase.budget}</td>
                </tr>
                ` : ''}
                ${businessCase.deadline ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Deadline:</strong></td>
                    <td>${new Date(businessCase.deadline).toLocaleDateString()}</td>
                </tr>
                ` : ''}
                ${businessCase.attachment_filename ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Attachment:</strong></td>
                    <td>${businessCase.attachment_filename}</td>
                </tr>
                ` : ''}
            </table>
        </div>
        
        <p>
            <a href="https://iod-dochub.iodparc.com/business-case-admin" 
               style="display: inline-block; padding: 10px 20px; background-color: #009688; color: white; text-decoration: none; border-radius: 5px;">
                Review in Dashboard
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Business Case System.
        </p>
    </div>
</body>
</html>
    `.trim();

    const approvalPayload: EmailPayload = {
        to: 'business_case_approval@iodparc.com',
        subject,
        body
    };

    const joannePayload: EmailPayload = {
        to: 'joanne@iodparc.com',
        subject,
        body
    };

    const [approvalSent, joanneSent] = await Promise.all([
        sendEmail(approvalPayload),
        sendEmail(joannePayload)
    ]);

    return approvalSent && joanneSent;
}

/**
 * Send approval notification to business case submitter
 */
export async function sendBusinessCaseApproval(businessCase: {
    owner_email: string;
    owner_name: string;
    opportunity_name: string;
    client: string;
    approved_by: string;
}): Promise<boolean> {
    const subject = `✅ Business Case Approved - ${businessCase.opportunity_name}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #28a745;">✅ Your Business Case Has Been Approved!</h2>
        
        <p>Hi ${businessCase.owner_name},</p>
        
        <p>Great news! Your business case has been approved.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Business Case Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Opportunity:</strong></td>
                    <td>${businessCase.opportunity_name}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Client:</strong></td>
                    <td>${businessCase.client}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Approved By:</strong></td>
                    <td>${businessCase.approved_by}</td>
                </tr>
            </table>
        </div>
        
        <p>You can now proceed with this opportunity. Good luck!</p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Business Case System.<br>
            If you have any questions, please contact business_case_approval@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: businessCase.owner_email,
        cc: 'business_case_approval@iodparc.com',
        subject,
        body
    });
}

/**
 * Send rejection notification to business case submitter
 */
export async function sendBusinessCaseRejection(businessCase: {
    owner_email: string;
    owner_name: string;
    opportunity_name: string;
    client: string;
    rejection_reason: string;
}): Promise<boolean> {
    const subject = `Business Case Rejected - ${businessCase.opportunity_name}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #dc3545;">Business Case Rejected</h2>
        
        <p>Hi ${businessCase.owner_name},</p>
        
        <p>Your business case has been rejected.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Business Case Details</h3>
            <p><strong>Opportunity:</strong> ${businessCase.opportunity_name}</p>
            <p><strong>Client:</strong> ${businessCase.client}</p>
        </div>
        
        <div style="background-color: #f8d7da; padding: 15px; border-radius: 5px; border-left: 4px solid #dc3545; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #721c24;">Reason for Rejection:</h3>
            <p style="margin: 0; white-space: pre-wrap;">${businessCase.rejection_reason}</p>
        </div>
        
        <p>If you wish to resubmit this business case, you can do so from the My Business Cases page.</p>
        
        <p>
            <a href="https://iod-dochub.iodparc.com/my-business-cases" 
               style="display: inline-block; padding: 10px 20px; background-color: #6c757d; color: white; text-decoration: none; border-radius: 5px;">
                View My Business Cases
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Business Case System.<br>
            If you have any questions, please contact business_case_approval@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: businessCase.owner_email,
        subject,
        body,
        cc: 'business_case_approval@iodparc.com'
    });
}

/**
 * Send crisis log notification to travel@iodparc.com
 */
export async function sendCrisisLogNotification(log: {
    submitter_name: string;
    submitter_email: string;
    incident_crisis_title: string;
    date_time_crisis_declared: string;
    duty_officer_on_call: string;
    crisis_management_team_lead: string;
    log_entries: Array<{
        time: string;
        actionTaken: string;
        byWhom: string;
        decisionOutcome: string;
        nextSteps: string;
    }>;
    closure_date_time?: string;
    after_action_review_completed: boolean;
    status: string;
}): Promise<boolean> {
    const subject = `🚨 Crisis Log - ${log.incident_crisis_title}`;
    
    const logEntriesHtml = log.log_entries.map((entry, idx) => `
        <tr style="border-bottom: 1px solid #ddd;">
            <td style="padding: 10px; vertical-align: top; font-weight: bold;">${entry.time}</td>
            <td style="padding: 10px; vertical-align: top;">${entry.actionTaken}</td>
            <td style="padding: 10px; vertical-align: top;">${entry.byWhom}</td>
            <td style="padding: 10px; vertical-align: top;">${entry.decisionOutcome || '-'}</td>
            <td style="padding: 10px; vertical-align: top;">${entry.nextSteps || '-'}</td>
        </tr>
    `).join('');
    
    const statusBadge = log.status === 'closed' 
        ? '<span style="background-color: #4caf50; color: white; padding: 5px 10px; border-radius: 3px;">CLOSED</span>'
        : '<span style="background-color: #f44336; color: white; padding: 5px 10px; border-radius: 3px;">ACTIVE</span>';
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 900px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #d32f2f;">🚨 Crisis Log Submitted</h2>
        
        <p>A crisis log has been submitted and requires review.</p>
        
        <div style="background-color: #ffebee; padding: 15px; border-radius: 5px; border-left: 4px solid #f44336; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #c62828;">Crisis Information</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Title:</strong></td>
                    <td>${log.incident_crisis_title}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Declared:</strong></td>
                    <td>${new Date(log.date_time_crisis_declared).toLocaleString()}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Duty Officer:</strong></td>
                    <td>${log.duty_officer_on_call}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Team Lead:</strong></td>
                    <td>${log.crisis_management_team_lead}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Status:</strong></td>
                    <td>${statusBadge}</td>
                </tr>
            </table>
        </div>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Action Log</h3>
            <table style="width: 100%; border-collapse: collapse;">
                <thead>
                    <tr style="background-color: #e0e0e0;">
                        <th style="padding: 10px; text-align: left; width: 10%;">Time</th>
                        <th style="padding: 10px; text-align: left; width: 25%;">Action Taken</th>
                        <th style="padding: 10px; text-align: left; width: 15%;">By Whom</th>
                        <th style="padding: 10px; text-align: left; width: 25%;">Decision/Outcome</th>
                        <th style="padding: 10px; text-align: left; width: 25%;">Next Steps</th>
                    </tr>
                </thead>
                <tbody>
                    ${logEntriesHtml}
                </tbody>
            </table>
        </div>
        
        ${log.closure_date_time ? `
        <div style="background-color: #e8f5e9; padding: 15px; border-radius: 5px; border-left: 4px solid #4caf50; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #2e7d32;">Closure Information</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Closure Date/Time:</strong></td>
                    <td>${new Date(log.closure_date_time).toLocaleString()}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>After-Action Review:</strong></td>
                    <td>${log.after_action_review_completed ? 'Yes' : 'No'}</td>
                </tr>
            </table>
        </div>
        ` : ''}
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Travel Management System.<br>
            Please review this crisis log and take appropriate action.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'travel@iodparc.com',
        subject,
        body
    });
}

/**
 * Send incident report notification to travel@iodparc.com
 */
export async function sendIncidentReportNotification(report: {
    submitter_name: string;
    submitter_email: string;
    incident_datetime: string;
    location: string;
    travellers_involved: string;
    incident_types: string[];
    incident_type_other_details?: string;
    description: string;
    immediate_actions?: string;
    parties_notified: string[];
    followup_actions?: string;
    completed_by: string;
    completed_date: string;
}): Promise<boolean> {
    const subject = `⚠️ Incident Report - ${report.location} - ${new Date(report.incident_datetime).toLocaleDateString()}`;
    
    const incidentTypesHtml = report.incident_types.length > 0 
        ? report.incident_types.map(type => `<span style="display: inline-block; background-color: #ff9800; color: white; padding: 3px 8px; border-radius: 3px; margin: 2px; font-size: 12px;">${type}</span>`).join(' ')
        : '<span style="color: #999;">None specified</span>';
    
    const partiesNotifiedHtml = report.parties_notified.length > 0
        ? report.parties_notified.map(party => `<span style="display: inline-block; background-color: #2196F3; color: white; padding: 3px 8px; border-radius: 3px; margin: 2px; font-size: 12px;">${party}</span>`).join(' ')
        : '<span style="color: #999;">None</span>';
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 700px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #ff5722;">⚠️ Incident Report Submitted</h2>
        
        <p>A new incident report has been submitted and requires review.</p>
        
        <div style="background-color: #ffebee; padding: 15px; border-radius: 5px; border-left: 4px solid #f44336; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #c62828;">Incident Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Date/Time:</strong></td>
                    <td>${new Date(report.incident_datetime).toLocaleString()}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Location:</strong></td>
                    <td>${report.location}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Traveller(s) Involved:</strong></td>
                    <td>${report.travellers_involved}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Incident Type(s):</strong></td>
                    <td>${incidentTypesHtml}</td>
                </tr>
                ${report.incident_type_other_details ? `
                <tr>
                    <td style="padding: 5px 0; vertical-align: top;"><strong>Other Details:</strong></td>
                    <td>${report.incident_type_other_details}</td>
                </tr>
                ` : ''}
            </table>
        </div>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Description of Incident</h3>
            <p style="white-space: pre-wrap; margin: 0;">${report.description}</p>
        </div>
        
        ${report.immediate_actions ? `
        <div style="background-color: #e3f2fd; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #1976d2;">Immediate Actions Taken</h3>
            <p style="white-space: pre-wrap; margin: 0;">${report.immediate_actions}</p>
        </div>
        ` : ''}
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Parties Notified</h3>
            <p>${partiesNotifiedHtml}</p>
        </div>
        
        ${report.followup_actions ? `
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Follow-up Actions Required</h3>
            <p style="white-space: pre-wrap; margin: 0;">${report.followup_actions}</p>
        </div>
        ` : ''}
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Report Completion</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Completed By:</strong></td>
                    <td>${report.completed_by}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Date:</strong></td>
                    <td>${new Date(report.completed_date).toLocaleDateString()}</td>
                </tr>
            </table>
        </div>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Travel Management System.<br>
            Please review this incident report and take appropriate action.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'travel@iodparc.com',
        subject,
        body
    });
}

/**
 * Send PPD submission notification to mark@iodparc.com and line manager
 */
export async function sendPPDSubmissionNotification(ppd: {
    owner_email: string;
    owner_name: string;
    manager_email: string;
    job_title?: string;
    activity?: string;
    development_need?: string;
    id: number;
}): Promise<boolean> {
    const subject = `🆕 New PPD Submitted - ${ppd.owner_name}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #009688;">New PPD Form Submitted</h2>
        
        <p>A new Personal and Professional Development form has been submitted and is awaiting review.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Submission Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Employee:</strong></td>
                    <td>${ppd.owner_name}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Email:</strong></td>
                    <td>${ppd.owner_email}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Line Manager:</strong></td>
                    <td>${ppd.manager_email}</td>
                </tr>
                ${ppd.job_title ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Job Title:</strong></td>
                    <td>${ppd.job_title}</td>
                </tr>
                ` : ''}
                ${ppd.activity ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Activity:</strong></td>
                    <td>${ppd.activity}</td>
                </tr>
                ` : ''}
            </table>
        </div>
        
        ${ppd.development_need ? `
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Development Need</h3>
            <p style="margin: 0; white-space: pre-wrap;">${ppd.development_need}</p>
        </div>
        ` : ''}
        
        <p>
            <a href="https://iod-dochub.iodparc.com/ppd-admin" 
               style="display: inline-block; padding: 10px 20px; background-color: #009688; color: white; text-decoration: none; border-radius: 5px;">
                Review in Dashboard
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD PPD System.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: 'mark@iodparc.com',
        cc: ppd.manager_email,
        subject,
        body
    });
}

/**
 * Send PPD submission confirmation to submitter
 */
export async function sendPPDSubmissionConfirmation(ppd: {
    owner_email: string;
    owner_name: string;
    manager_email: string;
    activity?: string;
}): Promise<boolean> {
    const subject = `✅ PPD Form Submitted Successfully`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #009688;">✅ Your PPD Form Has Been Submitted!</h2>
        
        <p>Hi ${ppd.owner_name},</p>
        
        <p>Thank you for submitting your Personal and Professional Development form.</p>
        
        <div style="background-color: #e8f5e9; padding: 15px; border-radius: 5px; border-left: 4px solid #4caf50; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #2e7d32;">What Happens Next?</h3>
            <p style="margin: 0;">Your form has been submitted and is awaiting review by:</p>
            <ul style="margin: 10px 0;">
                <li><strong>Mark</strong> (mark@iodparc.com)</li>
                <li><strong>Your Line Manager:</strong> ${ppd.manager_email}</li>
            </ul>
            <p style="margin: 10px 0 0 0;">You will receive an email notification when your PPD is reviewed.</p>
        </div>
        
        ${ppd.activity ? `
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Activity</h3>
            <p style="margin: 0;">${ppd.activity}</p>
        </div>
        ` : ''}
        
        <p>
            <a href="https://iod-dochub.iodparc.com/my-ppd" 
               style="display: inline-block; padding: 10px 20px; background-color: #009688; color: white; text-decoration: none; border-radius: 5px;">
                View My PPD Forms
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated confirmation from the IOD PPD System.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: ppd.owner_email,
        subject,
        body
    });
}

/**
 * Send PPD needs-discussion notification to employee
 */
export async function sendPPDNeedsDiscussion(ppd: {
    owner_email: string;
    owner_name: string;
    activity?: string;
    discussion_comments: string;
}): Promise<boolean> {
    const subject = `💬 PPD Needs Discussion - ${ppd.owner_name}`;

    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #0d6efd;">PPD Needs Discussion</h2>

        <p>Hi ${ppd.owner_name},</p>

        <p>Your Personal and Professional Development form needs further discussion before a final decision can be made.</p>

        ${ppd.activity ? `
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">PPD Details</h3>
            <p><strong>Activity:</strong> ${ppd.activity}</p>
        </div>
        ` : ''}

        <div style="background-color: #e7f1ff; padding: 15px; border-radius: 5px; border-left: 4px solid #0d6efd; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #084298;">Admin Comments:</h3>
            <p style="margin: 0; white-space: pre-wrap;">${ppd.discussion_comments}</p>
        </div>

        <p>Please review the comments and update your existing PPD form before resubmitting.</p>

        <p>
            <a href="https://iod-dochub.iodparc.com/my-ppd"
               style="display: inline-block; padding: 10px 20px; background-color: #0d6efd; color: white; text-decoration: none; border-radius: 5px;">
                Review My PPD Forms
            </a>
        </p>

        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD PPD System.<br>
            If you have any questions, please contact mark@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: ppd.owner_email,
        subject,
        body
    });
}

/**
 * Send PPD approval notification to employee
 */
export async function sendPPDApproval(ppd: {
    owner_email: string;
    owner_name: string;
    activity?: string;
    approved_by: string;
}): Promise<boolean> {
    const subject = `✅ PPD Approved - ${ppd.owner_name}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #28a745;">✅ Your PPD Has Been Approved!</h2>
        
        <p>Hi ${ppd.owner_name},</p>
        
        <p>Great news! Your Personal and Professional Development form has been approved.</p>
        
        <div style="background-color: #e8f5e9; padding: 15px; border-radius: 5px; border-left: 4px solid #4caf50; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #2e7d32;">PPD Details</h3>
            <table style="width: 100%;">
                ${ppd.activity ? `
                <tr>
                    <td style="padding: 5px 0;"><strong>Activity:</strong></td>
                    <td>${ppd.activity}</td>
                </tr>
                ` : ''}
                <tr>
                    <td style="padding: 5px 0;"><strong>Approved By:</strong></td>
                    <td>${ppd.approved_by}</td>
                </tr>
            </table>
        </div>
        
        <p>You can now proceed with your planned development activity. Good luck!</p>
        
        <p>
            <a href="https://iod-dochub.iodparc.com/my-ppd" 
               style="display: inline-block; padding: 10px 20px; background-color: #28a745; color: white; text-decoration: none; border-radius: 5px;">
                View My PPD Forms
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD PPD System.<br>
            If you have any questions, please contact mark@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: ppd.owner_email,
        subject,
        body
    });
}

/**
 * Send approved PPD notification to Hatty and Victoria (separate emails)
 */
export async function sendPPDApprovalStakeholderNotifications(ppd: {
    owner_name: string;
    activity?: string;
    provider_if_external?: string;
    duration?: string;
    cost_gbp?: number;
}): Promise<boolean> {
    const subject = `✅ PPD Approved - ${ppd.owner_name}`;

    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #28a745;">PPD Approved</h2>

        <p>A PPD form has been approved with the following details:</p>

        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Name:</strong></td>
                    <td>${ppd.owner_name}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Activity:</strong></td>
                    <td>${ppd.activity || '-'}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Provider:</strong></td>
                    <td>${ppd.provider_if_external || '-'}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Duration:</strong></td>
                    <td>${ppd.duration || '-'}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Cost:</strong></td>
                    <td>£${ppd.cost_gbp || 0}</td>
                </tr>
            </table>
        </div>

        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD PPD System.
        </p>
    </div>
</body>
</html>
    `.trim();

    const hattyPayload: EmailPayload = {
        to: 'hatty@iodparc.com',
        subject,
        body
    };

    const victoriaPayload: EmailPayload = {
        to: 'victoria@iodparc.com',
        subject,
        body
    };

    const [hattySent, victoriaSent] = await Promise.all([
        sendEmail(hattyPayload),
        sendEmail(victoriaPayload)
    ]);

    return hattySent && victoriaSent;
}

/**
 * Send PPD rejection notification to employee
 */
export async function sendPPDRejection(ppd: {
    owner_email: string;
    owner_name: string;
    activity?: string;
    rejection_reason?: string;
}): Promise<boolean> {
    const subject = `❌ PPD Requires Changes - ${ppd.owner_name}`;
    const hasRejectionReason = Boolean(ppd.rejection_reason?.trim());
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #dc3545;">PPD Requires Changes</h2>
        
        <p>Hi ${ppd.owner_name},</p>
        
        <p>Your Personal and Professional Development form requires some changes before it can be approved.</p>
        
        ${ppd.activity ? `
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">PPD Details</h3>
            <p><strong>Activity:</strong> ${ppd.activity}</p>
        </div>
        ` : ''}
        
        ${hasRejectionReason ? `
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Reason for Changes Required:</h3>
            <p style="margin: 0; white-space: pre-wrap;">${ppd.rejection_reason}</p>
        </div>
        ` : ''}
        
        <p>Please review the feedback and update your existing PPD form to address the points raised.</p>
        
        <p>
            <a href="https://iod-dochub.iodparc.com/my-ppd" 
               style="display: inline-block; padding: 10px 20px; background-color: #dc3545; color: white; text-decoration: none; border-radius: 5px;">
                Review My PPD Forms
            </a>
        </p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD PPD System.<br>
            If you have any questions, please contact mark@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: ppd.owner_email,
        subject,
        body
    });
}

/**
 * Send approval notification to event form submitter
 */
export async function sendEventApproval(eventForm: {
    submitter_email: string;
    submitter_name: string;
    event_title: string;
    dates: string;
    location: string;
    approved_by: string;
}): Promise<boolean> {
    const subject = `✅ Event Request Approved - ${eventForm.event_title}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #28a745;">✅ Your Event Request Has Been Approved!</h2>
        
        <p>Hi ${eventForm.submitter_name},</p>
        
        <p>Great news! Your event participation request has been approved.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Event Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Event:</strong></td>
                    <td>${eventForm.event_title}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Dates:</strong></td>
                    <td>${eventForm.dates}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Location:</strong></td>
                    <td>${eventForm.location}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Approved By:</strong></td>
                    <td>${eventForm.approved_by}</td>
                </tr>
            </table>
        </div>
        
        <p>You can now proceed with your event arrangements.</p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Event Approval System.<br>
            If you have any questions, please contact bgt@iodparc.com
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: eventForm.submitter_email,
        subject,
        body
    });
}

/**
 * Send rejection notification to event form submitter
 */
export async function sendEventRejection(eventForm: {
    submitter_email: string;
    submitter_name: string;
    event_title: string;
    dates: string;
    location: string;
    rejection_reason: string;
}): Promise<boolean> {
    const subject = `Event Request Rejected - ${eventForm.event_title}`;
    
    const body = `
<html>
<body style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">
    <div style="max-width: 600px; margin: 0 auto; padding: 20px; border: 1px solid #ddd; border-radius: 8px;">
        <h2 style="color: #dc3545;">Event Request Rejected</h2>
        
        <p>Hi ${eventForm.submitter_name},</p>
        
        <p>Your event participation request has been rejected.</p>
        
        <div style="background-color: #f8f9fa; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <h3 style="margin-top: 0;">Event Details</h3>
            <table style="width: 100%;">
                <tr>
                    <td style="padding: 5px 0;"><strong>Event:</strong></td>
                    <td>${eventForm.event_title}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Dates:</strong></td>
                    <td>${eventForm.dates}</td>
                </tr>
                <tr>
                    <td style="padding: 5px 0;"><strong>Location:</strong></td>
                    <td>${eventForm.location}</td>
                </tr>
            </table>
        </div>
        
        <div style="background-color: #fff3cd; padding: 15px; border-radius: 5px; border-left: 4px solid #ffc107; margin: 20px 0;">
            <h3 style="margin-top: 0; color: #856404;">Reason for Rejection:</h3>
            <p style="margin: 0; white-space: pre-wrap;">${eventForm.rejection_reason}</p>
        </div>
        
        <p>If you have any questions about this decision, please contact bgt@iodparc.com</p>
        
        <p style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #ddd; color: #666; font-size: 12px;">
            This is an automated message from the IOD Event Approval System.
        </p>
    </div>
</body>
</html>
    `.trim();

    return await sendEmail({
        to: eventForm.submitter_email,
        subject,
        body
    });
}
